import type { Dispatch, FC, SetStateAction } from 'react';
import type React from 'react';
import { createContext, useContext, useState } from 'react';

import type { PermissionGroup } from 'src/constants/modulePermission';
import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';

interface ModuleItemsContextValue {
  copyModules: string[];
  setCopyModules: Dispatch<SetStateAction<string[]>>;
  permissionRole: PermissionGroup;
  setPermissionRole: Dispatch<SetStateAction<number>>;
  hasAccess: boolean;
  setHasAccess: Dispatch<SetStateAction<boolean>>;
  readOnly: boolean;
  setReadOnly: Dispatch<SetStateAction<boolean>>;
  permissionChecked: boolean;
  setPermissionChecked: Dispatch<SetStateAction<boolean>>;
  orgUnits: ModuleOrPlanActionOrgUnit[];
  setOrgUnits: Dispatch<SetStateAction<ModuleOrPlanActionOrgUnit[]>>;
}

const ModuleItemsContext = createContext<ModuleItemsContextValue>({
  copyModules: [],
  setCopyModules() {},
  permissionRole: 1,
  setPermissionRole() {},
  hasAccess: false,
  setHasAccess() {},
  readOnly: false,
  setReadOnly() {},
  permissionChecked: false,
  setPermissionChecked() {},
  orgUnits: [],
  setOrgUnits() {},
});

interface ModuleItemsProviderProps {
  children: React.ReactNode;
}

export const useModuleItems = () => useContext(ModuleItemsContext);

export const ModuleItemsProvider: FC<ModuleItemsProviderProps> = ({
  children,
}) => {
  const [copyModules, setCopyModules] = useState<string[]>([]);
  const [permissionRole, setPermissionRole] = useState(1);
  const [hasAccess, setHasAccess] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [permissionChecked, setPermissionChecked] = useState(false);
  const [orgUnits, setOrgUnits] = useState<ModuleOrPlanActionOrgUnit[]>([]);

  return (
    <ModuleItemsContext.Provider
      value={{
        hasAccess: hasAccess,
        setHasAccess: setHasAccess,
        readOnly: readOnly,
        setReadOnly: setReadOnly,
        permissionChecked: permissionChecked,
        setPermissionChecked: setPermissionChecked,
        permissionRole: permissionRole,
        setPermissionRole: setPermissionRole,
        orgUnits: orgUnits,
        setOrgUnits: setOrgUnits,
        copyModules: copyModules,
        setCopyModules: setCopyModules,
      }}
    >
      {children}
    </ModuleItemsContext.Provider>
  );
};
